import { ModalPicker } from '@/components/extension/modal-picker'
import { Badge } from '@/components/ui/badge'
import { Card } from '@/components/ui/card'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import { useUserQuery } from '@/hooks/react-query/user/use-user'
import { ImageHelper } from '@/lib/helpers/image.helper'
import { SizeMaleSuggestion } from '@/types/size'
import { zodResolver } from '@hookform/resolvers/zod'
import { useTranslations } from 'next-intl'
import Image from 'next/image'
import { Dispatch, SetStateAction, useEffect, useMemo, useState } from 'react'
import { useForm } from 'react-hook-form'
import { z } from 'zod'

interface BodyType {
  id: string
  label: string
  image: string
}

interface FormattedSize {
  value: string | number
  label: string
}

interface SizeGuideMaleContentProps {
  height: number
  weight: number
  bodyType: string
  setHeight: Dispatch<SetStateAction<number>>
  setWeight: Dispatch<SetStateAction<number>>
  setBodyType: Dispatch<SetStateAction<string>>
  recommendedSize?: SizeMaleSuggestion | null
  isLoading?: boolean
  sizeChart?: string
}

const FormSchema = z.object({
  height: z.number().min(155).max(190),
  weight: z.number().min(40).max(90),
})

type FormValues = z.infer<typeof FormSchema>

export const SizeGuideMaleContent = ({
  height,
  weight,
  bodyType,
  setHeight,
  setWeight,
  setBodyType,
  recommendedSize,
  isLoading = false,
  sizeChart,
}: SizeGuideMaleContentProps) => {
  const [openHeightPicker, setOpenHeightPicker] = useState(false)
  const [openWeightPicker, setOpenWeightPicker] = useState(false)
  const { data: userData } = useUserQuery()
  const t = useTranslations('product_detail.instruction_size')

  const form = useForm<FormValues>({
    resolver: zodResolver(FormSchema),
    defaultValues: {
      height,
      weight,
    },
  })

  useEffect(() => {
    if (userData && userData.height && userData.weight) {
      setHeight(userData.height)
      setWeight(userData.weight)
      setBodyType('normal')

      form.reset({
        height: userData.height,
        weight: userData.weight,
      })
    }
  }, [userData, form, setBodyType, setHeight, setWeight])

  const onSubmit = (values: FormValues) => {
    setHeight(values.height)
    setWeight(values.weight)
  }

  const bodyTypes: BodyType[] = [
    {
      id: 'thin',
      label: t('body_type.thin'),
      image: '/image/November2023/mceclip1_23.jpg',
    },
    {
      id: 'normal',
      label: t('body_type.normal'),
      image: '/image/November2023/mceclip3_97.jpg',
    },
    {
      id: 'fat',
      label: t('body_type.fat'),
      image: '/image/November2023/mceclip0_45.png',
    },
  ]

  // Định dạng và sắp xếp size với ưu tiên cho trường size
  const formattedSizes: FormattedSize[] = useMemo(() => {
    if (!recommendedSize) return []

    const sizeEntries: FormattedSize[] = []

    const SIZE_LABEL = {
      shirt: t('size_label.shirt'),
      pant: t('size_label.pant'),
      dress_shirt: t('size_label.dress_shirt'),
      jeans: t('size_label.jeans'),
    }

    // Thêm size chung trước nếu có
    if (recommendedSize.size) {
      // nếu sizeChart chứa key trong SIZE_LABEL thì sử dụng value tương ứng
      const sizeLabel =
        sizeChart?.includes(recommendedSize.size) &&
        SIZE_LABEL[recommendedSize.size as keyof typeof SIZE_LABEL]

      if (sizeLabel) {
        sizeEntries.push({ value: recommendedSize.size, label: sizeLabel })
      }
    }

    // Thêm các size cụ thể
    if (recommendedSize.pant) {
      sizeEntries.push({
        value: recommendedSize.pant,
        label: t('size_label.pant'),
      })
    }

    if (recommendedSize.shirt) {
      sizeEntries.push({
        value: recommendedSize.shirt,
        label: t('size_label.shirt'),
      })
    }

    // Xử lý ưu tiên cho áo sơ mi
    if (recommendedSize.dress_shirt) {
      sizeEntries.push({
        value: recommendedSize.dress_shirt,
        label: t('size_label.dress_shirt'),
      })
    } else if (recommendedSize.dress_shirt_size) {
      sizeEntries.push({
        value: recommendedSize.dress_shirt_size,
        label: t('size_label.dress_shirt'),
      })
    }

    if (recommendedSize.jeans) {
      sizeEntries.push({
        value: recommendedSize.jeans,
        label: t('size_label.jeans'),
      })
    }

    return sizeEntries
  }, [recommendedSize, sizeChart, t])

  return (
    <div>
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className='space-y-3'>
          <div className='flex items-center gap-6'>
            <FormField
              control={form.control}
              name='height'
              render={({ field }) => (
                <FormItem className='w-full'>
                  <FormLabel>{t('form.height')}</FormLabel>
                  <FormControl>
                    <div className='relative w-full'>
                      <Input
                        {...field}
                        readOnly
                        className='rounded-lg border-neutral-300 px-4 py-3'
                        placeholder={t('form.placeholder.height')}
                        suffix={
                          <p className='font-sans text-sm font-normal leading-5 text-neutral-300'>
                            {t('form.unit.cm')}
                          </p>
                        }
                        onClick={() => setOpenHeightPicker(true)}
                      />
                    </div>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name='weight'
              render={({ field }) => (
                <FormItem className='w-full'>
                  <FormLabel>{t('form.weight')}</FormLabel>
                  <FormControl>
                    <div className='relative w-full'>
                      <Input
                        {...field}
                        readOnly
                        className='rounded-lg border-neutral-300 px-4 py-3'
                        placeholder={t('form.placeholder.weight')}
                        suffix={
                          <p className='font-sans text-sm font-normal leading-5 text-neutral-300'>
                            {t('form.unit.kg')}
                          </p>
                        }
                        onClick={() => setOpenWeightPicker(true)}
                      />
                    </div>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
        </form>
      </Form>

      {/* Body types */}
      <div className='mb-8 mt-4 grid grid-cols-3 gap-3'>
        {bodyTypes.map((type) => (
          <Card
            key={type.id}
            className={`cursor-pointer bg-gray-100 transition-all ${
              bodyType === type.id ? 'ring-2 ring-blue-500' : ''
            }`}
            onClick={() => setBodyType(type.id)}
          >
            <div className='flex justify-center'>
              <Image
                src={ImageHelper.getImageUrl(type.image)}
                alt={type.label}
                width={100}
                height={100}
                className='h-full w-full rounded-lg object-cover'
              />
            </div>
          </Card>
        ))}
      </div>

      <div className='mt-6'>
        <h3 className='mb-4 text-lg font-bold'>
          {t('suggestion.title')}
          {isLoading && (
            <span className='ml-2 text-sm italic text-gray-500'>
              {t('suggestion.loading_inline')}
            </span>
          )}
        </h3>
        {formattedSizes.length > 0 ? (
          <div className='flex flex-wrap gap-2'>
            {formattedSizes.map((size, index) => (
              <Badge
                key={index}
                variant='default'
                className='bg-neutral-900 px-3 py-0.5 text-sm font-bold text-white'
              >
                {size.value} - {size.label}
              </Badge>
            ))}
          </div>
        ) : (
          <p className='italic text-gray-500'>
            {isLoading
              ? t('suggestion.calculating_size')
              : t('suggestion.choose_body_type_hint')}
          </p>
        )}
      </div>

      {/* Height ModalPicker */}
      {openHeightPicker && (
        <ModalPicker
          onClose={() => setOpenHeightPicker(false)}
          title={t('modal.pick_height')}
          pickers={[
            {
              title: t('form.height'),
              key: 'height',
              options: Array.from({ length: 36 }, (_, i) => {
                const value = 155 + i
                return {
                  label: `${value}cm`,
                  value: `${value}`,
                }
              }),
              selected: String(height),
              className: 'h-36',
            },
          ]}
          onConfirm={(selected) => {
            const value = parseInt(String(selected.height || '155'))
            form.setValue('height', value)
            setHeight(value)
            setOpenHeightPicker(false)
          }}
        />
      )}

      {/* Weight ModalPicker */}
      {openWeightPicker && (
        <ModalPicker
          onClose={() => setOpenWeightPicker(false)}
          title={t('modal.pick_weight')}
          pickers={[
            {
              title: t('form.weight'),
              key: 'weight',
              options: Array.from({ length: 51 }, (_, i) => {
                const value = 40 + i
                return {
                  label: `${value}kg`,
                  value: `${value}`,
                }
              }),
              selected: String(weight),
              className: 'h-36',
            },
          ]}
          onConfirm={(selected) => {
            const value = parseInt(String(selected.weight || '40'))
            form.setValue('weight', value)
            setWeight(value)
            setOpenWeightPicker(false)
          }}
        />
      )}
    </div>
  )
}
